import React from 'react';
import { ExternalLink } from 'lucide-react';
import SectionHeading from './SectionHeading';

const Projects = function({ lang, isDark, sections, projects }) {
  return (
    <section id="projects" className="mb-10" aria-labelledby="proj-heading">
      <div className="section-card">
        <SectionHeading id="proj-heading" isDark={isDark}>{sections.projects}</SectionHeading>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-9">
        {projects.map((item, i) => (
          <article key={i} className={`group flex flex-col rounded-lg -mx-3 px-3 py-2 transition-colors duration-300 print:hover:bg-transparent ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-100/80'}`}>
            {!item.noPreview && item.image && (
              <div className={`relative mb-3 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                <img
                  src={item.image}
                  alt={`${item.title} preview`}
                  loading="lazy"
                  className="w-full aspect-video object-cover object-top"
                />
                {item.status === 'development' && (
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-[14px] -left-[52px] w-[180px] -rotate-45 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold uppercase tracking-widest text-center py-1 shadow-lg">
                      In Dev
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-1.5">
              <h3 className={`text-[16px] font-bold ${isDark ? 'text-slate-100' : 'text-slate-900'} flex items-center flex-wrap gap-2`}>
                {item.link ? (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline hover:text-blue-600 dark:hover:text-blue-400 inline-flex items-center gap-1 transition-colors"
                  >
                    {item.title}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  item.title
                )}
                {item.status === 'production' && (
                  <span className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                    <span className="w-1 h-1 bg-current rounded-full" /> Live
                  </span>
                )}
                {item.status === 'deploying' && (
                  <span className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                    <span className="w-1 h-1 bg-current rounded-full animate-pulse" /> Pending Deploy
                  </span>
                )}
                {item.status === 'development' && (
                  <span className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    <span className="w-1 h-1 bg-current rounded-full animate-pulse" /> In Dev
                  </span>
                )}
              </h3>
              <span className={`text-[11px] font-mono tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'} whitespace-nowrap`}>
                {item.period}
              </span>
            </div>

            {item.impact && (
              <p className={`text-[13px] italic opacity-85 mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                {item.impact}
              </p>
            )}

            {item.techStack ? (
              <div className="flex flex-wrap gap-2 mb-3 mt-1">
                {item.techStack.replace('Tech: ', '').split(', ').map((tech, tIdx) => (
                  <span key={tIdx} className={`px-2 py-0.5 rounded-md text-[11px] font-bold tracking-wide border ${isDark ? 'bg-blue-500/10 text-blue-300 border-blue-500/20' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                    {tech}
                  </span>
                ))}
              </div>
            ) : item.tech && (
              <div className="flex flex-wrap gap-2 mb-3 mt-1">
                {item.tech.map((tech, tIdx) => (
                  <span key={tIdx} className={`px-2 py-0.5 rounded-md text-[11px] font-bold tracking-wide border ${isDark ? 'bg-blue-500/10 text-blue-300 border-blue-500/20' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                    {tech}
                  </span>
                ))}
              </div>
            )}

            {item.highlights && (
              <ul className="space-y-2 ml-4 list-outside list-disc marker:text-slate-400">
                {item.highlights.map((h, j) => {
                  const highlightedText = h.replace(/(\d+\+?\s*(?:tables|REST API endpoints|admin pages|major events)?)/g, (match) =>
                    `<span class="font-bold ${isDark ? 'text-blue-300' : 'text-blue-600'}">${match}</span>`
                  );
                  return (
                    <li
                      key={j}
                      className={`text-[14px] leading-relaxed pl-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                      dangerouslySetInnerHTML={{ __html: highlightedText }}
                    />
                  );
                })}
              </ul>
            )}
          </article>
        ))}
        </div>
      </div>
    </section>
  );
}

export default React.memo(Projects);
